/* eslint-disable prefer-destructuring */
import React, { createContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

const SNACKBAR_TYPES = {
	SUCCESS: 'success',
	ERROR: 'error',
	WARNING: 'warning',
	INFO: 'info',
};

const SnackbarsContext = createContext();

/**
 * For general usage and docs, see this (requires VPN):
 * `http://stellar.devposlavu.com/components/snackbar`
 */

const SnackbarsProvider = props => {
	const initialState = {
		success: {
			active: false,
			message: '',
		},
		error: {
			active: false,
			message: '',
		},
		warning: {
			active: false,
			message: '',
		},
		info: {
			active: false,
			message: '',
		},
		prevActiveType: '',
		prevActiveMessage: '',
		action: [],
	};

	const [state, setSnackbarState] = useState(initialState);
	const value = useMemo(() => ({ state, setSnackbarState }), [state]);

	return <SnackbarsContext.Provider value={value} {...props} />;
};

/**
 * Hooks API.
 * Note that this Hook is also used for the implementation of
 * the `Snackbar` component here in the `utils` module.
 */
const useSnackbar = () => {
	const context = React.useContext(SnackbarsContext);

	if (!context) {
		throw new Error('useSnackbar must be used within a SnackbarProvider');
	}

	const { state, setSnackbarState } = context;

	const updater = (type, message, options = {}) => {
		const { active = true, callback, action = [] } = options;

		if (callback !== undefined) {
			callback();
		}

		setSnackbarState(state => {
			let prevActiveType = state.prevActiveType;

			/* `prevActiveType` holds a reference to the previous snackbar type
			 * that was open. This is useful to ensure that during the transition
			 * as the snackbar closes, the color (determined by the `variant`)
			 * remains the same */
			if (state.prevActiveType !== type) {
				prevActiveType = type;
			}

			return {
				...state,
				[type]: {
					active,
					message,
				},
				prevActiveType,
				action,
			};
		});
	};

	return {
		state,
		updater,
	};
};

/**
 * This function encapsulates the state logic performed when the
 * `updater` function is called from the Render Prop or HOC
 * context consumers below.
 * Note that the `useSnackbar` Hook does not use it. */
const handleUpdate = params => {
	const {
		setSnackbarState,
		type,
		message,
		options: { active = true, callback, action = [] },
	} = params;

	if (callback !== undefined) {
		callback();
	}

	/**
	 * `prevActiveMessage` is initially an empty string and is reset to an
	 * empty string when a snackbar is dismissed. So, each time a consumer
	 * activates it, prevActiveMessage is set to the value of the current
	 * `message` passed. This allows the `Snackbar` component to access
	 * this value during the transition as the snackbar closes and
	 * maintain the same width. */
	setSnackbarState(state => ({
		...state,
		[type]: {
			active,
			message,
		},
		prevActiveMessage: message,
		action,
	}));
};

/**
 * Render Prop API
 */
const SnackbarsConsumer = ({ children }) => (
	<SnackbarsContext.Consumer>
		{context => {
			if (context === undefined) {
				throw new Error(
					'SnackbarsConsumer must be used within a SnackbarsProvider'
				);
			}

			const { state, setSnackbarState } = context;
			const updater = (type, message, options = {}) => {
				handleUpdate({ type, message, options, setSnackbarState });
			};

			return children({
				state,
				updater,
			});
		}}
	</SnackbarsContext.Consumer>
);

SnackbarsConsumer.propTypes = {
	children: PropTypes.func,
};

/**
 * Higher-order component API
 */
const SnackbarsContextConsumer = SnackbarsContext.Consumer;
const withContext = Consumer => mapContextToProps => WrappedComponent => {
	const ContextComponent = props => (
		<Consumer>
			{context => {
				if (context === undefined) {
					throw new Error(
						'SnackbarsConsumer must be used within a SnackbarsProvider'
					);
				}

				const { state, setSnackbarState } = context;
				const updater = (type, message, options = {}) => {
					handleUpdate({ type, message, options, setSnackbarState });
				};

				return (
					<WrappedComponent
						{...props}
						{...mapContextToProps({ state, updater }, props)}
					/>
				);
			}}
		</Consumer>
	);

	ContextComponent.displayName = `ContextComponent(${WrappedComponent.displayName ||
		WrappedComponent.name ||
		'Component'})`;

	return ContextComponent;
};

const withSnackbarsContext = withContext(SnackbarsContextConsumer);

/**
 * Don't export the `SnackbarsContext` directly. Instead we export the
 * provider and different ways to consume this context.
 * This puts a bit more control over the API to ensure it is being
 * used as intended. */
export {
	SNACKBAR_TYPES,
	SnackbarsProvider,
	SnackbarsConsumer,
	withSnackbarsContext,
	useSnackbar,
};
